/**
 * Event Bus - Decoupled communication between modules
 *
 * This provides a simple pub/sub system for module communication
 * without direct dependencies between modules.
 */

// Event type constants
export const Events = {
  // Data events
  DATA_LOADED: "data:loaded", // Initial data loaded (document switch, first load)
  DATA_CHANGED: "data:changed", // Any data change (for re-render)
  CRDT_PROJECTION_UPDATED: "crdt:projection_updated", // CRDT rebuilt Store (not initial load): { data, changes, origin } (changes: engine/core/tree-reconcile.js, null when not the same map; origin: "local", "remote", "batch"…)

  // Granular node operations (for CRDT sync)
  NODE_ADDED: "node:added", // { parentId, node, index }
  NODE_UPDATED: "node:updated", // { nodeId, updates, oldValues }
  NODE_REMOVED: "node:removed", // { nodeId, parentId, node }
  NODE_MOVED: "node:moved", // { nodeId, oldParentId, newParentId, index }

  // Selection/focus
  NODE_SELECTED: "node:selected", // { nodeId }
  NODE_FOCUSED: "node:focused", // { nodeId }

  // View events
  VIEW_CHANGED: "view:changed", // { view, previousView }
  VIEW_RENDER: "view:render", // Request re-render

  // Undo/redo state (Y.UndoManager)
  UNDO_STATE_CHANGED: "undo:stateChanged", // { docId, canUndo, canRedo }

  // Sync events
  SYNC_CONNECTED: "sync:connected", // WebSocket connected
  SYNC_DISCONNECTED: "sync:disconnected",
  SYNC_REMOTE_UPDATE: "sync:remote_update", // Remote change received
  SYNC_ERROR: "sync:error",
  SYNC_PAUSED: "sync:paused",
  SYNC_PAUSE_CHANGED: "sync:pause_changed",

  // Document notes events (F3.1: separate from tree changes)
  DOC_NOTES_CHANGED: "docnotes:changed", // { origin: 'local'|'remote' }

  // Execution map (Today lists) changed — not a tree change, no Store rebuild
  ROUTINES_CHANGED: "routines:changed",
  EXECUTION_CHANGED: "execution:changed", // { origin: 'local'|'remote' }

  // Auth events
  AUTH_LOGIN: "auth:login", // User logged in
  AUTH_LOGOUT: "auth:logout", // User logged out
  SERVER_BUSY_CHANGED: "sync:serverBusy", // { busy } the server refused a save as busy (busy: true), or confirmed one since (false)
  SESSION_ENDED: "auth:sessionEnded", // { reason } this device's session ended: a socket closed 4401, or the server refuses it (engine/sync/session-check.js)
  SESSION_RESUMED: "auth:sessionResumed", // signed in again in place (interfaces/classic/ui/session-resume.js): the sockets reconnect

  // UI events
  MODAL_OPEN: "modal:open", // { modalId, data }
  MODAL_CLOSE: "modal:close", // { modalId }
  SAVE_STATUS: "save:status", // { status: 'saving'|'saved'|'error' }
  STORAGE_WARNING: "storage:warning", // { type: 'quota_exceeded', message }
  QUOTA_CHANGED: "quota:changed",
  QUOTA_REJECTED: "quota:rejected",
  QUOTA_MAP_OPENED: "quota:mapOpened",
  // { docId, level: 'normal'|'warning'|'full', percent }: an open map's size
  // against the limit changed (engine/services/map-size.js).
  MAP_SIZE_CHANGED: "mapSize:changed",
  // { docId, offline, count }: the server will not store changes made here
  // (this user may now only view the map). engine/sync/refused-changes.js.
  CHANGES_REFUSED: "sync:changesRefused",
  // The open map's state changed in the engine (engine/map-state.js): its
  // tree, id, sharing; the interface's Store follows it
  // { state, previous, reason: "open"|"tree"|"local-fields"|"role"|"map-id"|"reset", … }
  MAP_STATE_CHANGED: "mapState:changed",
  // A map was opened in the engine (engine/map-state.js open) { docId }
  MAP_OPENED: "mapState:opened",
  // The open map was closed (engine/open-map.js closeMap) { docId }
  MAP_CLOSED: "mapState:closed",
  // Who else has the open map open (engine/open-map.js) { users }
  PRESENCE_CHANGED: "map:presenceChanged",
  // The server says a newer version exists (engine/sync/app-version.js)
  // { version, level, message }; interfaces/classic/ui/update-manager.js decides what to show.
  UPDATE_AVAILABLE: "app:updateAvailable",
  // A screen of the page shown (interfaces/classic/ui/auth/screens.js showScreen) { screenId }
  SCREEN_SHOWN: "screen:shown",
  // The start is done: signed in with the first map open, or the signed-out
  // start, also after signing out (interfaces/classic/managers/auth-manager.js,
  // interfaces/classic/managers/offline-mode.js) {}
  APP_STARTED: "app:started",
  // Something the engine tells the person (engine/host.js notify):
  // { code, level, message, data, duration }; the interface shows it.
  ENGINE_NOTICE: "engine:notice",
  // The maps this person shares changed (the engine; the vault panel's
  // cache follows) { owned: Array }
  OWNED_SHARES_CHANGED: "shares:ownedChanged",

  // Theme events
  THEME_CHANGED: "theme:changed", // { uiTheme }
  COLOR_THEME_CHANGED: "theme:color_changed", // { colorTheme, colors }

  // Sharing events
  INVITATIONS_UPDATED: "invitations:updated", // { count, newCount }
  INVITATION_ACCEPTED: "invitation:accepted", // { invitationId, shareId }
  INVITATION_DECLINED: "invitation:declined", // { invitationId }
  SHARE_REMOVED: "share:removed", // { shareId }
  SHARE_REVOKED: "share:revoked", // { shareId, recoveredDocId, wasActive }
  SHARE_INVITATION_RESPONDED: "share:invitation_responded", // { docId, invitationId, accepter } - invitation accepted by recipient
  SHARE_LINK_CHANGED: "share:link_changed", // { docId } - a map's link made, changed, turned off (sync/link-sharing.js)
  SHARE_LINK_OPENED: "share:link_opened", // { docId, role, kept } - this page opened a map by its link (kept in the vault, or for the page only)
  SHARE_LINK_GONE: "share:link_gone", // { docId } - the link a map was opened by no longer works (turned off, replaced, the map deleted)
  EDIT_BLOCKED: "edit:blocked", // { action, reason } - edit blocked due to read-only

  // Share revocation events (to break circular dependency between crdt-bridge and notifications)
  DISMISS_SHARE_NOTIFICATION: "share:dismiss_notification", // { shareId } - dismiss revoked notification
  REQUEST_SHARE_REVOCATION: "share:request_revocation", // { shareId, title, options } - request revocation from notification
  SHARE_REVOCATION_READY: "share:revocation_ready", // { shareId, title, preStopTreeData } - active doc revoked, ready for user-confirmed recovery

  // Document error events
  DOCUMENT_CORRUPTED: "document:corrupted", // { docId, docName, reason } - CRDT data is invalid/corrupted

  // User CRDT events (vault index, preferences sync)
  USER_CRDT_UPDATED: "userCrdt:updated", // User CRDT data changed
  USER_CRDT_STATE_CHANGED: "userCrdt:stateChanged", // Sync state changed
  USER_CRDT_ERROR: "userCrdt:error", // Sync error occurred
  USER_CRDT_MIGRATED: "userCrdt:migrated", // Data migrated to User CRDT
  USER_NOTIFICATION_RECEIVED: "user:notificationReceived", // Real-time notification from server
  USER_NOTIFICATION_DISMISSED: "user:notificationDismissed", // Notification dismissed on another device (id)

  // Vault index events
  VAULT_INDEX_UPDATED: "vault:indexUpdated", // Vault index refreshed from CRDT { source: 'remote'|'local' }
  // Maps this device knew were online that the server no longer has: moved
  // to another device or deleted there { docIds: string[] } (engine/sync/known-online.js)
  MAPS_GONE_ONLINE: "maps:goneOnline",
  // A new online map was kept on this device: the server could not be
  // reached. It goes online once it can (interfaces/classic/ui/map-storage.js) { docId, title }
  MAP_KEPT_ON_DEVICE: "maps:keptOnDevice",

  // Header server indicator redrawn (engine/core/server-status.js) { level, text, hint }
  SERVER_STATUS_CHANGED: "server:statusChanged",
  WAITING_FILES_CHANGED: "attachments:waitingChanged", // files attached offline, not sent yet { count }
  STORAGE_POLICY_CHANGED: "storage:policyChanged", // whether the browser may delete stored data { mayBeCleared }

  // Access-request events
  ACCESS_REQUESTS_UPDATED: "accessRequests:updated", // Pending set changed — payload: { pending: Set<docId> }
  OPEN_SHARE_MODAL_FOR_REQUEST: "share:openModalForRequest", // User clicked "Grant access" on an access_request notification — { docId, requesterUsername, docTitle }
};

// Subscriber storage
const subscribers = new Map();

/**
 * Subscribe to an event
 * @param {string} event - Event name from Events constants
 * @param {Function} callback - Function to call when event fires
 * @returns {Function} Unsubscribe function
 */
export function on(event, callback) {
  if (!subscribers.has(event)) {
    subscribers.set(event, new Set());
  }
  subscribers.get(event).add(callback);

  // Return unsubscribe function
  return () => {
    const subs = subscribers.get(event);
    if (subs) {
      subs.delete(callback);
    }
  };
}

/**
 * Subscribe to an event, but only fire once
 * @param {string} event - Event name
 * @param {Function} callback - Function to call
 * @returns {Function} Unsubscribe function
 */
/** How many listen to `event` (for checks: listeners an interface leaves behind). */
export function listenerCount(event) {
  return subscribers.get(event)?.size || 0;
}

export function once(event, callback) {
  const unsubscribe = on(event, (data) => {
    unsubscribe();
    callback(data);
  });
  return unsubscribe;
}

/**
 * Emit an event to all subscribers
 * @param {string} event - Event name
 * @param {*} data - Data to pass to subscribers
 */
export function emit(event, data = null) {
  const subs = subscribers.get(event);
  if (subs) {
    subs.forEach((callback) => {
      try {
        callback(data);
      } catch (err) {
        console.error(`[Events] Error in ${event} handler:`, err);
      }
    });
  }
}
